import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';

@Component({
  tag: 'lar-inner-range-door',
  styleUrl: 'inner-range-door.scss',
  shadow: true
})
export class InnerRangeDoor {
  @Element() el!: HTMLLarInnerRangeDoorElement;
  @Prop() doorType?: string;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'door';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * Component specific options (see above)
   */
  @Prop() options?: any;
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() alarm: boolean = false;
  @State() loading: boolean = true;
  @State() locked: boolean = false;
  private node!: HTMLLarNodeElement;

  /**
   * Larva input message
   */
  @Method()
  async input(data: any) {
    if (data && data.State) {
      this.locked = data.State === 'Locked';
      this.loading = false;
    }
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.loading = true;
    this.output.emit();
  }

  handleChange() {
    this.loading = true;
    return this.output.emit({ command: this.doorType === 'toggle' ? 'toggle' : this.locked ? 'unlock' : 'lock' });
  }

  render() {
    const iconSmall = this.locked ? 'lock' : 'unlock';
    const componentProps = {
      onClick: () => this.handleChange(),
      value: this.doorType === 'toggle' ? 'door.toggleLockUnlock' : this.locked ? 'door.unlock' : 'door.lock',
      icon: iconSmall,
      disabled: this.loading,
      color: this.colorInputs || this.color
    };
    return (
      <lar-node
        value={this.locked ? 'door.locked' : 'door.unlocked'}
        hideTitles={this.hideTitles}
        iconSmall={this.locked ? 'lock' : 'unlock'}
        colorIconSmall={this.allowIndicationAutoColoring && !this.loading ? (this.locked? 'success': 'danger') : this.colorIconSmall}
        icon={this.icon}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        nodeSize={this.nodeSize}
        log={this.log}
        loading={this.loading}
        component="lar-inner-range-area-door-content"
        componentProps={componentProps}
        >
        <slot />
      </lar-node>
    );
  }
}
